import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { QuestionProps } from "./Props";

import { useToast } from "@aidkitorg/component-library";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import { usePost } from "../API";
import InterfaceContext from "../Context";
import { useDebouncedCallback } from "../Hooks/Debounce";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse } from "../Util";

export function SearchSelectLargeDictionaryQuestion(props: QuestionProps) {
  type LgDictOption = Record<string, any> | 'not_found';
  const context = useContext(InterfaceContext);
  const searchLargeDictionary = usePost('/program/search_large_dictionary');

  const L = useLocalizedStrings();
  const { toast } = useToast();
  const targetField = props["Target Field"]!;
  const setInfoKey = useCallback(props.setInfoKey, [props.info]);

  const [query, setQuery] = useState('');
  const [isSearching, setSearching] = useState(false);
  const debouncedSetQuery = useDebouncedCallback((query: string) => setQuery(query));

  const metadata = safeParse(props['Metadata'] || '{}');
  const { max_options, search_type, search_type: { allowNotFound, columns }} = metadata;

  const [filteredOptions, setFilteredOptions] = useState<Record<string, any>[]>([]);
  const [selectedOption, setSelectedOption] = useState<LgDictOption | {}>(
    props.info[targetField] === 'not_found'
      ? allowNotFound?.customOptionLabel?.[context.lang] || L.questions.search_select.not_found
      : safeParse(props.info[targetField] || '{}')
  );

  // TODO: handle language change?

  useEffect(() => {
    const search = async () => {
      setSearching(true);
      try {
        const res: any = await searchLargeDictionary({
          term: query,
          limit: max_options,
          baseTableName: search_type.baseTableName,
        });

        if (res.error) {
          throw res.error;
        } else {
          setFilteredOptions(res);
        }
      } catch (err) {
        console.log(err);
        toast({
          description: L.questions.search_select.options_error,
          variant: 'error'
        });
      } finally {
        setSearching(false);
      }
    };

    if (query.length > 2) {
      search();
    } else {
      setFilteredOptions([]);
    }
  }, [query]);

  function handleChange(e: any) {
    if (e.target.value) {
      debouncedSetQuery(e.target.value) 
    } else {
      setQuery('');
      updateValue('');
    }
  }

  function updateValue(value: LgDictOption | '') {
    let newValue = '';
    if (value === 'not_found') {
      newValue = 'not_found';
    } else if (value) {
      newValue = JSON.stringify(value);
    };

    setSelectedOption(value || {});
    setFilteredOptions([]);
    setInfoKey(
      targetField,
      newValue,
      !!newValue,
      false
    );
  }

  const formattedContent = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
  });

  if ((props['Additional Options'] || []).indexOf('Hidden') !== -1) {
    return <span></span>;
  }

  const displayValueForInput = (option: LgDictOption) => {
    if (option === 'not_found') {
      return allowNotFound?.customOptionLabel?.[context.lang] || L.questions.search_select.not_found;
    }
    return option[columns.searchColumn] || '';
  };

  const NotFoundOption = () =>
    !isSearching ? (
      <Combobox.Option
        className="relative cursor-pointer px-4 py-2 text-gray-900"
        value="not_found"
      >
        {allowNotFound.customOptionLabel?.[context.lang] || L.questions.search_select.not_found}
      </Combobox.Option>
    ) : (
      <></>
    );

  return (
    <fieldset>
      <legend>
        {formattedContent}
      </legend>
      <Combobox value={selectedOption} onChange={(o: LgDictOption) => {updateValue(o)}} disabled={props.blockQuestionEdits}>
        <div className="relative">
          <Combobox.Input
            className={`max-w-full block border-solid p-2 rounded-md border-2 focus:ring-gray-400 focus:border-gray-400"
              ${props.info[targetField]
      ? 'border-green-400 ring-green-400'
      : props["Additional Options"]?.includes('Optional')
        ? 'border-gray-200 ring-gray-200'
        : 'border-red-200 ring-red-400'
    }`}
            style={{ width: '26rem' }}
            displayValue={displayValueForInput}
            onChange={handleChange}
          />
          {query?.length >= 3 && (
            <Transition
              as={Fragment}
              leave="transition ease-in duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery('')}
            >
              <Combobox.Options
                className="max-w-full m-0 py-2 px-0 bg-white border border-gray-200 rounded-md focus:outline-none z-50"
                style={{ width: '26rem' }}
              >
                {isSearching ? (
                  <div className="select-none px-4 py-2">
                    {L.dashboard.searching}
                  </div>
                ) : filteredOptions?.length === 0 ? (
                  !allowNotFound && (
                    <div className="select-none px-4 py-2">
                      {L.dashboard.no_results}
                    </div>
                  )
                ) : (
                  filteredOptions?.map((option: any, i: number) => (
                    <Combobox.Option
                      key={i}
                      className={({ active }) => 
                        `relative cursor-pointer px-4 py-2 ${active ? 'bg-gray-100' : 'text-gray-900'}`
                      }
                      value={option}
                    >
                      {({ active, selected }) => (
                        <>
                          <span className={`block ${selected ? 'font-medium' : 'font-normal'} ${active ? 'whitespace-normal' : 'truncate'}`}>
                            {option[columns.searchColumn]}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-0.5">
                              <CheckIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
                {allowNotFound && <NotFoundOption />}
              </Combobox.Options>
            </Transition>
          )}
        </div>
      </Combobox>
    </fieldset>
  );
}